import Add from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { TextCellWithTooltip } from "components/DataGridCell";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import {
  WasteInvoicesTableQuery,
  WasteInvoicesTableQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2, useOmnisearchStore } from "hooks/omnisearch-v2";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";

import { usePeriodDisplayTokens } from "../invoiceTrackingPeriod";
import { WASTE_INVOICES_QUERY } from "../queries";
import {
  formatInvoiceDate,
  formatInvoiceMoney,
  formatServicePeriod,
  INVOICE_TRACKING_PAGE_SIZE,
  INVOICE_TRACKING_PAGE_SIZES,
} from "../utils";

type InvoiceRow = WasteInvoicesTableQuery["wasteInvoices"]["items"][number];

const INITIAL_SORT: GridSortModel = [{ field: "invoiceDate", sort: "desc" }];
const FILTER_KEYS: FilterKey[] = [
  { key: "invoiceNumber", header: "Invoice #" },
  {
    key: "vendor",
    header: "Vendor",
    filterKeyType: "wasteInvoiceVendor",
  },
  { key: "invoiceDate", header: "Invoice date", filterKeyType: "date" },
  { key: "servicePeriod", header: "Service period", filterKeyType: "date" },
  { key: "invoiceTotal", header: "Total", filterKeyType: "number" },
];
const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Invoice",
    fields: ["invoiceNumber", "vendor", "invoiceDate", "servicePeriod", "invoiceTotal"],
  },
];

const INVOICE_COLUMNS: OmnisearchGridColDef<InvoiceRow>[] = [
  {
    field: "invoiceNumber",
    headerName: "Invoice #",
    minWidth: 150,
    flex: 0.8,
    filterable: false,
  },
  {
    field: "vendor",
    headerName: "Vendor",
    minWidth: 170,
    flex: 1,
    filterable: false,
    valueGetter: (_, row) => row.vendor.name,
    renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
  },
  {
    field: "invoiceDate",
    headerName: "Invoice date",
    minWidth: 125,
    flex: 0.65,
    filterable: false,
    valueGetter: (_, row) => formatInvoiceDate(row.invoiceDate),
  },
  {
    field: "servicePeriodStart",
    headerName: "Service period",
    minWidth: 205,
    flex: 1,
    filterable: false,
    valueGetter: (_, row) => formatServicePeriod(row.servicePeriodStart, row.servicePeriodEnd),
  },
  {
    field: "invoiceTotal",
    headerName: "Total",
    minWidth: 130,
    flex: 0.65,
    type: "number",
    filterable: false,
    valueGetter: (_, row) => formatInvoiceMoney(row.invoiceTotal),
  },
  {
    field: "lineCount",
    headerName: "Lines",
    minWidth: 90,
    flex: 0.4,
    type: "number",
    filterable: false,
  },
];

const getInvoiceItems = (data: WasteInvoicesTableQuery) => data.wasteInvoices.items;
const getInvoiceCount = (data: WasteInvoicesTableQuery) => data.wasteInvoices.count;
/** Carries the reader's filters, so leaving a row and coming back lands on the
 * list they left rather than on the default period. */
const invoiceRowLink = (row: InvoiceRow, omnisearch: string) =>
  omnisearch ? `./${row.id}?${new URLSearchParams({ omnisearch })}` : `./${row.id}`;

export function InvoicesTable({
  tenantId,
  canImport,
  onImport,
}: {
  tenantId: string;
  canImport: boolean;
  onImport: () => void;
}) {
  const buildVariables = useCallback(
    (
      search: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): WasteInvoicesTableQueryVariables => ({
      tenantId,
      search,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((item) => ({ field: item.field, sort: item.sort })),
    }),
    [tenantId]
  );

  const commandButtons = useMemo(
    () =>
      canImport
        ? [
            <Button
              key="import-invoices"
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={onImport}
            >
              Import invoices
            </Button>,
          ]
        : [],
    [canImport, onImport]
  );

  // The bar above the tabs owns the period, so the grid does not offer a second
  // pill for it.
  const hiddenDisplayTokens = [...usePeriodDisplayTokens()];
  const { omnisearch } = useOmnisearchStore();
  const getRowLink = useCallback(
    (row: InvoiceRow) => invoiceRowLink(row, omnisearch),
    [omnisearch]
  );

  return (
    <OmnisearchDataGridV2
      hiddenDisplayTokens={hiddenDisplayTokens}
      persistenceKey="waste-invoices-table"
      tenantId={tenantId}
      query={WASTE_INVOICES_QUERY}
      getItems={getInvoiceItems}
      getCount={getInvoiceCount}
      buildVariables={buildVariables}
      columns={INVOICE_COLUMNS}
      pageSizeOptions={INVOICE_TRACKING_PAGE_SIZES}
      initialPageSize={INVOICE_TRACKING_PAGE_SIZE}
      initialSortModel={INITIAL_SORT}
      additionalFilterKeys={FILTER_KEYS}
      filterGroups={FILTER_GROUPS}
      commandButtons={commandButtons}
      getRowLink={getRowLink}
      disableRowSelectionOnClick
      noRowsMessage="No waste invoices have been imported for this organization."
      noDataButtonText={canImport ? "Import invoices" : undefined}
      onNoDataButtonClick={canImport ? onImport : undefined}
    />
  );
}
